import { useEffect, useRef, useState } from 'react';
import type { Attachment } from 'sdk';
import { type AttachmentMeta, type ResolveOptions, resolveAttachmentUrl } from '~/modules/attachment/helpers/resolve-url';
import type { BlobVariant } from '~/modules/attachment/offline/attachments-db';

interface UseAttachmentUrlResult {
  url: string | null;
  isLocal: boolean;
  resolvedVariant: BlobVariant | null;
  isLoading: boolean;
  error: Error | null;
}

/** Resolves an attachment URL local-first, falling back to a cloud URL. */
export function useAttachmentUrl(
  attachment: (AttachmentMeta & Pick<Attachment, 'id'>) | null | undefined,
  { preferredVariant }: ResolveOptions = {},
): UseAttachmentUrlResult {
  const [result, setResult] = useState<UseAttachmentUrlResult>({
    url: null,
    isLocal: false,
    resolvedVariant: null,
    isLoading: !!attachment,
    error: null,
  });

  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!attachment) {
      setResult((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    let cancelled = false;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const resolve = async () => {
      setResult((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const resolved = await resolveAttachmentUrl(attachment.id, attachment, { preferredVariant });

        if (!cancelled) {
          if (resolved?.isLocal) blobUrlRef.current = resolved.url;
          setResult({
            url: resolved?.url ?? null,
            isLocal: resolved?.isLocal ?? false,
            resolvedVariant: resolved?.variant ?? null,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setResult({
            url: null,
            isLocal: false,
            resolvedVariant: null,
            isLoading: false,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [attachment?.id, attachment?.keys.original, preferredVariant]);

  return result;
}
